/**
 * Scans management page with scan list, creation form, and scheduling.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useScans, useCreateScan, useCancelScan, useSchedules, useCreateSchedule, useUpdateSchedule, useDeleteSchedule } from '@/hooks/useScans';
import { getAssets } from '@/api/assets';
import ScanList from '@/components/scans/ScanList';
import ScanConfig from '@/components/scans/ScanConfig';
import ScanScheduler from '@/components/scans/ScanScheduler';
import ScanDetails from '@/components/scans/ScanDetails';
import Modal from '@/components/common/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { formatDate } from '@/utils/formatters';
import type { Scan, ScanSchedule } from '@/types/scan';
import { apiError } from '@/utils/apiError';

export default function ScansPage() {
  const { isCustomerAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [showScanForm, setShowScanForm] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScanSchedule | null>(null);
  const [selectedScan, setSelectedScan] = useState<Scan | null>(null);
  const [activeTab, setActiveTab] = useState<'scans' | 'schedules'>('scans');

  const { data: scans, isLoading: scansLoading,error:scansError } = useScans();
  const { data: schedules, isLoading: schedulesLoading,error:schedulesError } = useSchedules();
  const { data: assets } = useQuery({ queryKey: ['assets'], queryFn: () => getAssets() });

  const createScan = useCreateScan();
  const cancelScan = useCancelScan();
  const createSchedule = useCreateSchedule();
  const updateSchedule = useUpdateSchedule();
  const deleteSchedule = useDeleteSchedule();

  function handleCancel(scan: Scan) {
    if (confirm(`Cancel scan "${scan.name}"?`)) {
      cancelScan.mutate(scan.id);
    }
  }

  function handleToggleSchedule(schedule: ScanSchedule) {
    updateSchedule.mutate({ id: schedule.id, is_active: !schedule.is_active });
  }

  function handleDeleteSchedule(schedule: ScanSchedule) {
    if (confirm(`Delete schedule "${schedule.name}"? This cannot be undone.`)) {
      deleteSchedule.mutate(schedule.id);
    }
  }

  if (scansLoading) return <LoadingSpinner label="Loading scans..." />;

  return (
    <div className="space-y-6">
      {(scansError||schedulesError||cancelScan.error||updateSchedule.error||deleteSchedule.error)&&<p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(scansError||schedulesError||cancelScan.error||updateSchedule.error||deleteSchedule.error)}</p>}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Scans</h1>
        {isCustomerAdmin && (
          <div className="flex gap-2">
            <button onClick={() => setShowScheduleForm(true)} className="btn-secondary">
              Schedule
            </button>
            <button onClick={() => setShowScanForm(true)} className="btn-primary">
              New Scan
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b">
        <button
          onClick={() => setActiveTab('scans')}
          className={`border-b-2 pb-2 text-sm font-medium ${
            activeTab === 'scans' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Scans ({scans?.length ?? 0})
        </button>
        <button
          onClick={() => setActiveTab('schedules')}
          className={`border-b-2 pb-2 text-sm font-medium ${
            activeTab === 'schedules' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          Schedules ({schedules?.length ?? 0})
        </button>
      </div>

      {/* Tab content */}
      {activeTab === 'scans' ? (
        <ScanList
          onSelect={setSelectedScan}
          scans={scans ?? []}
          onCancel={handleCancel}
          canManage={isCustomerAdmin}
        />
      ) : schedulesLoading ? (
        <LoadingSpinner label="Loading schedules..." />
      ) : (
        <div className="space-y-3">
          {schedules?.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">No scheduled scans.</p>
          ) : (
            schedules?.map((schedule: ScanSchedule) => (
              <div key={schedule.id} className="card flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900">{schedule.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    <span className="capitalize">{schedule.scan_type.replace('_', ' ')}</span>
                    {' · '}Cron: <code className="bg-gray-100 px-1 rounded">{schedule.cron_expression}</code>
                    {' · '}Next: {formatDate(schedule.next_run_at)}
                    {schedule.last_run_at && <>{' · '}Last: {formatDate(schedule.last_run_at)}</>}
                  </p>
                </div>
                {isCustomerAdmin && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleToggleSchedule(schedule)}
                      disabled={updateSchedule.isPending}
                      className={`rounded-full px-3 py-1 text-xs font-medium border ${
                        schedule.is_active
                          ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                          : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {schedule.is_active ? 'Active' : 'Paused'}
                    </button>
                    <button
                      onClick={() => setEditingSchedule(schedule)}
                      className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule)}
                      disabled={deleteSchedule.isPending}
                      className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* New scan modal */}
      <Modal isOpen={!!selectedScan} onClose={() => setSelectedScan(null)} title={selectedScan?.name ?? 'Scan details'} size="lg">
        {selectedScan && <ScanDetails scan={scans?.find((scan) => scan.id === selectedScan.id) ?? selectedScan} />}
      </Modal>

      <Modal isOpen={showScanForm} onClose={() => setShowScanForm(false)} title="Configure Scan" size="lg">
        {createScan.error&&<p role="alert" className="mb-3 text-red-700">{apiError(createScan.error)}</p>}
        <ScanConfig
          assets={assets ?? []}
          onSubmit={(payload) => {
            createScan.mutate(payload, {
              onSuccess: () => {
                setShowScanForm(false);
                queryClient.invalidateQueries({ queryKey: ['scans'] });
              },
            });
          }}
          onCancel={() => setShowScanForm(false)}
          isSubmitting={createScan.isPending}
        />
      </Modal>

      {/* Schedule modal */}
      <Modal isOpen={showScheduleForm} onClose={() => setShowScheduleForm(false)} title="Create Schedule" size="lg">
        {createSchedule.error&&<p role="alert" className="mb-3 text-red-700">{apiError(createSchedule.error)}</p>}
        <ScanScheduler
          assets={assets ?? []}
          onSubmit={(payload) => {
            createSchedule.mutate(payload, {
              onSuccess: () => setShowScheduleForm(false),
            });
          }}
          onCancel={() => setShowScheduleForm(false)}
          isSubmitting={createSchedule.isPending}
        />
      </Modal>

      {/* Edit schedule modal */}
      <Modal
        isOpen={editingSchedule !== null}
        onClose={() => setEditingSchedule(null)}
        title="Edit Schedule"
        size="lg"
      >
        {editingSchedule && (
          <ScanScheduler
            assets={assets ?? []}
            initialSchedule={editingSchedule}
            onSubmit={(payload) => {
              updateSchedule.mutate(
                { id: editingSchedule.id, ...payload },
                { onSuccess: () => setEditingSchedule(null) },
              );
            }}
            onCancel={() => setEditingSchedule(null)}
            isSubmitting={updateSchedule.isPending}
          />
        )}
      </Modal>
    </div>
  );
}
